import React from 'react';
import { useRouteMatch } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import Chip from '~/components/core/Atomic/Chip/Chip';
import { PERMISSION_ACTIONS, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import { useHasPermission } from '~/components/hooks/useHasPermission';
import InlineIconButton from '~/components/InlineIconButton';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { OrganizationModel } from '~/components/types/organization-types';
import { ADMIN_ROUTES } from '~/routesConstants';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled } from '~/Utils';

import copies from '../copies.json';

// Both places that mount the editors - the Operations cards and the internal admin's organization tabs -
// mount the Global Configuration screen beside them, so its route is a sibling of the editor's own.
// Deep-linked to the tab that manages the field the badge sits on, so the screen opens where the
// configurator can act rather than on whichever tab it defaults to. The policy editor is the named
// case and every other host is an FNOL one: incident details and supplementary information are both
// managed on the FNOL tab.
const getGlobalConfigurationUrl = (editorUrl: string) => {
  const tab = editorUrl.includes(ADMIN_ROUTES.POLICY_CONFIGURATION)
    ? ADMIN_ROUTES.GLOBAL_CONFIGURATION.POLICY
    : ADMIN_ROUTES.GLOBAL_CONFIGURATION.FNOL;
  return `${editorUrl.replace(/\/[^/]*\/?$/, '')}/${ADMIN_ROUTES.GLOBAL_CONFIGURATION.MAIN}/${tab}`;
};

const GlobalFieldBadge: React.FC = () => {
  const { organization } = useSysconfig() as { organization: OrganizationModel };
  const { url } = useRouteMatch();
  const hasGlobalFieldsScope = useHasPermission({
    action: PERMISSION_ACTIONS.SYSCONFIG_GLOBAL_FIELDS,
    verb: PERMISSION_VERBS.FULL,
  });
  const badge = <Chip size="small" label={copies.global_fields.badge} />;

  // Offered only where that screen would open for this configurator: it is mounted with the flag on and
  // gated on the global-fields scope, which whoever edits this configuration may not hold.
  if (!hasGlobalFieldsScope || !isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.GLOBAL_CONFIGURED_FIELDS)) {
    return badge;
  }

  // A new tab, never an in-place navigation: this card sits in a form the configurator is part way
  // through, and routing away from it discards every unsaved edit on the screen. Same affordance as
  // ClaimLink's, so the icon means here what it means on a claim.
  const openInNewTab = (event: React.MouseEvent) => {
    // The badge sits in the card header, which toggles on click.
    event.stopPropagation();
    window.open(getGlobalConfigurationUrl(url), '_blank', 'noopener');
  };

  return (
    <div className="flex items-center">
      {badge}
      <div className="pl-4">
        <InlineIconButton
          // No defaultColor: the component already falls back to 'action', and its JS signature
          // types the prop as undefined, so passing it is both redundant and a type error here.
          icon={OpenInNewIcon}
          tooltipTitle={copies.global_fields.open_in_new_tab}
          // A real, focusable <button> carrying both the label and the handler. Without it the button role
          // sits on an unfocusable wrapping span while onClick is spread onto the icon inside, so only a
          // pointer click on the icon itself reaches the handler: a keyboard or screen reader never does.
          useIconButton
          onClick={openInNewTab}
        />
      </div>
    </div>
  );
};

export default GlobalFieldBadge;
